"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Lock, Search, X } from "lucide-react";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useSchool } from "@/lib/contexts/SchoolContext";
import { cn } from "@/lib/utils";
import { runSearch } from "@/lib/search";
import { logSearch, logSearchSelect } from "@/lib/search/log";
import { getPrimaryColor } from "@/components/home/AppGrid";
import type { ProductId } from "@/lib/constants/products";
import type { SearchContext, SearchResult } from "@/lib/search/types";

const LAYER_LABEL: Record<SearchResult["layer"], string> = {
  destination: "Go to",
  record: "Records",
  assistant: "Answers",
};

interface HubSearchProps {
  /** Opens the hub picker on a hub the owner does not have yet. */
  onSelectLockedHub: (id: ProductId) => void;
}

/**
 * Cross-app search on the home page.
 *
 * Today it answers "where is that?" from a client-side index of every hub and
 * page the user can reach, plus — for owners — the hubs they could add. It
 * renders results grouped by layer so the record and assistant layers can
 * appear alongside without a UI change.
 */
export function HubSearch({ onSelectLockedHub }: HubSearchProps) {
  const router = useRouter();
  const { user } = useAuth();
  const { schools, hasProductAccess, getProductRole, isOwner, isAdmin } = useSchool();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [active, setActive] = useState(0);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isPlatformAdmin = user?.user_metadata?.role === "platform_admin";

  const ctx = useMemo<SearchContext>(
    () => ({
      hasProductAccess: (id: ProductId) => hasProductAccess(id),
      getProductRole: (id: ProductId) => getProductRole(id),
      isOwner,
      isAdmin,
      isPlatformAdmin,
      multiSchool: schools.length > 1,
    }),
    // hasProductAccess/getProductRole close over the same context state these
    // primitives describe, so keying on the primitives avoids a render loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOwner, isAdmin, isPlatformAdmin, schools.length],
  );

  useEffect(() => {
    let cancelled = false;
    let settled: SearchResult[] = [];
    runSearch(query, ctx, (partial) => {
      if (cancelled) return;
      settled = partial;
      setResults(partial);
    });
    setActive(0);

    // Log once the typing stops, so "pto" is not recorded as p, pt, pto.
    const timer = setTimeout(() => {
      if (!cancelled) logSearch(query, settled);
    }, 900);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, ctx]);

  // Cmd/Ctrl+K from anywhere on the page.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => () => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
  }, []);

  const open = focused && query.trim().length > 0;

  const go = (result: SearchResult) => {
    logSearchSelect(query, result, results.indexOf(result));
    setQuery("");
    setResults([]);
    inputRef.current?.blur();
    // A locked hub has no page to open — send the owner to the picker instead.
    if (result.locked && result.hub) onSelectLockedHub(result.hub);
    else router.push(result.href);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (query) setQuery("");
      else inputRef.current?.blur();
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const chosen = results[active];
      if (chosen) go(chosen);
    }
  };

  // Group by layer and lock state, preserving the already-sorted order within
  // each. Locked results sort last, so this never interleaves.
  const groups = results.reduce<
    { layer: SearchResult["layer"]; locked: boolean; items: SearchResult[] }[]
  >((acc, r) => {
    const locked = !!r.locked;
    const last = acc[acc.length - 1];
    if (last && last.layer === r.layer && last.locked === locked) last.items.push(r);
    else acc.push({ layer: r.layer, locked, items: [r] });
    return acc;
  }, []);

  let flatIndex = -1;

  return (
    <div className="relative max-w-xl mx-auto mb-8 md:mb-10">
      <div
        className={cn(
          "flex items-center gap-2.5 rounded-2xl bg-white/80 backdrop-blur-sm px-4 h-12",
          "ring-1 ring-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.05),0_6px_16px_-8px_rgba(0,0,0,0.18)]",
          "transition-shadow duration-200",
          focused && "ring-black/[0.12] shadow-[0_2px_4px_rgba(0,0,0,0.06),0_12px_28px_-10px_rgba(0,0,0,0.22)]",
        )}
      >
        <Search className="h-4 w-4 text-gray-400 shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (blurTimer.current) clearTimeout(blurTimer.current);
            setFocused(true);
          }}
          // Let a click on a result land before the panel unmounts.
          onBlur={() => {
            blurTimer.current = setTimeout(() => setFocused(false), 120);
          }}
          onKeyDown={onKeyDown}
          placeholder="Search for anything — try “time off” or “fire drill”"
          aria-label="Search the app"
          className="flex-1 min-w-0 bg-transparent text-sm text-gray-800 placeholder:text-gray-400 outline-none"
        />
        {query ? (
          <button
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
            className="text-gray-400 hover:text-gray-700 transition-colors shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <kbd className="hidden sm:block text-[10px] font-medium text-gray-400 border border-gray-200 rounded px-1.5 py-0.5 shrink-0">
            ⌘K
          </kbd>
        )}
      </div>

      {open && (
        <div className="absolute z-40 left-0 right-0 mt-2 rounded-2xl bg-white ring-1 ring-black/[0.08] shadow-[0_8px_40px_-8px_rgba(0,0,0,0.28)] overflow-hidden max-h-[22rem] overflow-y-auto">
          {results.length === 0 ? (
            <p className="px-4 py-5 text-sm text-gray-500">
              Nothing matches “{query}”.
            </p>
          ) : (
            groups.map((group) => (
              <div key={`${group.layer}-${group.locked}`}>
                {(groups.length > 1 || group.locked) && (
                  <p className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    {group.locked ? "Not in your plan" : LAYER_LABEL[group.layer]}
                  </p>
                )}
                {group.items.map((r) => {
                  flatIndex += 1;
                  const i = flatIndex;
                  const Icon = r.icon;
                  return (
                    <button
                      key={r.id}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(r)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors",
                        i === active ? "bg-gray-100" : "hover:bg-gray-50",
                      )}
                    >
                      <Icon
                        className={cn("h-4 w-4 shrink-0", r.locked && "opacity-50")}
                        style={{ color: r.hub ? getPrimaryColor(r.hub) : "#6b7280" }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-gray-900 truncate">{r.title}</span>
                        <span className="block text-xs text-gray-500 truncate">
                          {r.locked ? "Add to your plan" : r.subtitle}
                        </span>
                      </span>
                      {r.locked && <Lock className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
